import {
  at,
  get,
  groupBy,
  isDate,
  isObject,
  mapValues,
  sortBy,
} from 'lodash-es'
import { IColumn, IGroup } from 'office-ui-fabric-react'

// Sorts an array of items by a specified column
export function sortByColumn<T>(items: T[], sortColumn?: IColumn) {
  // If a sort column is specified and it is sorted, sort the items
  if (sortColumn?.fieldName && sortColumn.isSorted) {
    const sortedItems = sortColumn
      ? (sortBy(items, (item) => {
          const fieldValue = get(item, getSortFieldNames(sortColumn, item))
          if (typeof fieldValue === 'string') {
            return fieldValue.toLocaleLowerCase()
          }
          return fieldValue
        }) as T[])
      : items

    // If the sort column is sorted in descending order, reverse the sorted items
    return sortColumn.isSortedDescending ? sortedItems.reverse() : sortedItems
  }

  // If no sort column is specified or it is not sorted, return the items as-is
  return items
}

// Gets the sort field name for a specified column and item
function getSortFieldNames(col: IColumn, item?: any): string {
  if (col.fieldName === 'person') {
    if (!item?.person && item?.unknownPerson?.surName) {
      return 'unknownPerson.surName'
    }
    return 'person.name'
  }
  if (col.fieldName === 'provingGround') {
    return 'provingGround.title'
  }
  return col.fieldName!
}

// Gets the group value for a specified item and column
function getGroupValue<T>(item: T, groupColumn: IColumn) {
  if (groupColumn?.data?.getGroupValue) {
    return groupColumn.data.getGroupValue(item, groupColumn)
  }

  const value = item[groupColumn?.fieldName ?? ''] ?? ''

  if (isObject(value)) {
    return JSON.stringify(value)
  }

  if (isDate(value)) {
    return value.toLocaleString()
  }

  return value
}

// Groups and sorts an array of items by a specified column
export function groupSortByColumn<T>(
  items: T[],
  alwaysCollapsed: boolean,
  groupColumn?: IColumn,
  sortColumn?: IColumn,
  itemOffset: number = 0
) {
  let sortedItems = sortByColumn(items, sortColumn)

  // If a group column is specified and it is grouped, group the items
  if (groupColumn?.fieldName && groupColumn.isGrouped) {
    const groups: IGroup[] = []

    // Group the items by the group column value
    const itemsByGroup = mapValues(
      groupBy(items, (x) => getGroupValue(x, groupColumn)),
      (y) => sortByColumn(y, sortColumn)
    )

    sortedItems = []

    // For each group, add a group object to the groups array and add the group items to the sorted items array
    for (const [groupKey] of Object.entries(itemsByGroup)) {
      const groupItems = itemsByGroup[groupKey]

      groups.push({
        key: groupKey,
        name: groupKey,
        isCollapsed: alwaysCollapsed,
        startIndex: sortedItems.length + itemOffset,
        count: groupItems.length,
      })

      sortedItems = [...sortedItems, ...(groupItems as any[])]
    }

    // Return the groups and sorted items
    return { groups, sortedItems }
  }

  // If no group column is specified or it is not grouped, return only the sorted items
  return { groups: undefined, sortedItems }
}

// Groups and sorts an array of items by a specified parent column and child column
export function groupSortByColumnAndParent<T>(
  items: T[],
  alwaysCollapsed: boolean,
  parentColumn: string,
  noGroupText: string = 'no group',
  groupColumn?: IColumn,
  sortColumn?: IColumn
) {
  // Sort the items by the parent column value
  const itemsByGroupName = sortBy(
    items,
    (item) => item[parentColumn] ?? 'zzzzz'
  )
  const parentGroups: IGroup[] = []
  const itemsByGroup = mapValues(
    // Group the items by the parent column value
    groupBy(
      itemsByGroupName,
      (x) => at(x as any, parentColumn)?.[0] ?? noGroupText
    ),
    (y) => sortByColumn(y, sortColumn)
  )

  let sortedParentItems: T[] = []

  // For each parent group, add a parent group object to the parent groups array and add the child groups and items to the sorted parent items array
  for (const [parentGroupKey] of Object.entries(itemsByGroup)) {
    const parentGroupItems = itemsByGroup[parentGroupKey] as T[]

    const { groups, sortedItems } = groupSortByColumn(
      parentGroupItems,
      alwaysCollapsed,
      groupColumn,
      sortColumn,
      sortedParentItems.length
    )

    if (groups) {
      groups.forEach((g) => (g.level = 2))
    }
    parentGroups.push({
      key: parentGroupKey,
      name: parentGroupKey,
      isCollapsed: alwaysCollapsed,
      startIndex: sortedParentItems.length,
      count: parentGroupItems.length,
      children: groups,
      level: 1,
    })

    sortedParentItems = [...sortedParentItems, ...sortedItems]
  }

  // Return the parent groups and sorted parent items
  return { groups: parentGroups, sortedItems: sortedParentItems }
}

// Ensures that the isSorted, isSortedDescending, and isGrouped properties of each column in an array of columns are set correctly based on the specified group and sort columns
export function ensureGroupSortColumns(
  columns: IColumn[],
  groupColumn?: IColumn,
  sortColumn?: IColumn
) {
  return columns.map((column) => {
    // If the column is the sort column, set the isSorted and isSortedDescending properties
    if (sortColumn?.key === column.key) {
      column.isSorted = true
      column.isSortedDescending = !!sortColumn.isSortedDescending
    } else {
      // Otherwise, clear the isSorted and isSortedDescending properties
      column.isSorted = false
      column.isSortedDescending = false
    }

    // If the column is the group column, set the isGrouped property
    if (groupColumn?.key === column.key) {
      column.isGrouped = groupColumn.isGrouped
    } else {
      // Otherwise, clear the isGrouped property
      column.isGrouped = false
    }

    // Return the modified column
    return column
  })
}
